import React from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface CTraderAccount {
  id: string;
  csp_account_name: string;
  csp_account_email: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  total_api_connections: number;
  total_trading_accounts: number;
  broker?: string;
  prop_firm?: string;
}

interface EditCTraderProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CTraderAccount;
}

interface FormValues {
  csp_account_email: string;
  csp_account_name: string;
  platformType: 'broker' | 'prop_firm';
  platform: string;
}

export function EditCTraderProfileDialog({
  open,
  onOpenChange,
  account
}: EditCTraderProfileDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const brokers = ['Coinstrat Pro', 'Fxpro', 'XM', 'IC Market'];
  const propFirms = ['FTMO', 'AI Prop'];

  // Determine initial platform type and name
  const initialPlatformType = account.broker ? 'broker' : 'prop_firm';
  const initialPlatform = account.broker || account.prop_firm || '';
  
  const form = useForm<FormValues>({
    defaultValues: {
      csp_account_email: account.csp_account_email,
      csp_account_name: account.csp_account_name,
      platformType: initialPlatformType,
      platform: initialPlatform
    }
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const updateData = {
        csp_account_email: values.csp_account_email,
        csp_account_name: values.csp_account_name,
        // Set the appropriate field based on platform type
        broker: values.platformType === 'broker' ? values.platform : null,
        prop_firm: values.platformType === 'prop_firm' ? values.platform : null
      };

      const { error } = await supabase
        .from('csp_accounts')
        .update(updateData)
        .eq('id', account.id);

      if (error) throw error;
      
      toast({
        title: "Profile updated",
        description: "Your cTrader profile has been successfully updated."
      });
      
      queryClient.invalidateQueries({ queryKey: ['ctraderAccounts'] });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit cTrader Profile</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="csp_account_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email of Profile</FormLabel>
                  <FormControl>
                    <Input type="email" required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="csp_account_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name of Profile</FormLabel>
                  <FormControl>
                    <Input required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="platformType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Platform Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="broker">Broker</SelectItem>
                      <SelectItem value="prop_firm">Prop Firm</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="platform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {form.watch('platformType') === 'broker' ? 'Broker' : 'Prop Firm'}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {form.watch('platformType') === 'broker'
                        ? brokers.map(broker => (
                            <SelectItem key={broker} value={broker}>
                              {broker}
                            </SelectItem>
                          ))
                        : propFirms.map(firm => (
                            <SelectItem key={firm} value={firm}>
                              {firm}
                            </SelectItem>
                          ))
                      }
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
